/**
 * Modal — accessible dialog with focus trap + portal escape.
 *
 * Why a portal? `position: fixed inset-0` resolves relative to the nearest
 * ancestor with a `transform`, not the viewport. AppShell's sidebar
 * wrapper uses `translate-x-0`, so any modal rendered as a descendant
 * of the sidebar (e.g. ContactModal) would anchor to the sidebar instead
 * of the viewport without a portal. Always render to <body>.
 *
 * Focus trap implementation:
 *   - On open: save the previously-focused element, focus the first
 *     focusable child (or the dialog itself)
 *   - Tab + Shift+Tab cycle through focusable descendants
 *   - On close: restore focus to the previously-focused element
 *
 * Other behaviors:
 *   - Esc closes (unless dismissable={false})
 *   - Click on backdrop closes (unless dismissable={false})
 *   - Body scroll locked while open
 *   - ARIA: role=dialog, aria-modal=true, aria-labelledby (if title)
 *
 * Sizes: sm | md (default) | lg | xl | full
 *
 * Sub-parts:
 *   <Modal.Header> — title + close button
 *   <Modal.Body>   — content
 *   <Modal.Footer> — actions
 *
 * Usage:
 *   <Modal isOpen={open} onClose={close} title="Delete case?">
 *     <Modal.Body>Are you sure? This cannot be undone.</Modal.Body>
 *     <Modal.Footer>
 *       <Button variant="ghost" onClick={close}>Cancel</Button>
 *       <Button variant="danger" onClick={confirm}>Delete</Button>
 *     </Modal.Footer>
 *   </Modal>
 */

import { useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import { HiOutlineXMark } from 'react-icons/hi2'

const SIZES = {
  sm:   'max-w-sm',
  md:   'max-w-md',
  lg:   'max-w-2xl',
  xl:   'max-w-4xl',
  full: 'max-w-[min(100vw-2rem,1400px)]',
}

const FOCUSABLE = [
  'a[href]',
  'button:not([disabled])',
  'textarea:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',')

export default function Modal({
  isOpen,
  onClose,
  title,
  size = 'md',
  dismissable = true,
  initialFocusRef,
  className = '',
  children,
}) {
  const dialogRef = useRef(null)
  const previousFocusRef = useRef(null)
  const titleId = useRef(`modal-title-${Math.random().toString(36).slice(2, 8)}`).current

  // Latest-value refs so the open/close effect below can depend ONLY on
  // `isOpen`. Parents almost always pass inline `onClose` arrows, so its
  // identity changes on every parent render; with `onClose` in the effect
  // deps, ANY re-render while the modal was open (e.g. each keystroke in a
  // controlled input inside the modal) re-ran the effect and yanked focus
  // back to the first focusable element — the "cursor disappears after
  // every letter" bug.
  const onCloseRef = useRef(onClose)
  const dismissableRef = useRef(dismissable)
  const initialFocusPropRef = useRef(initialFocusRef)
  onCloseRef.current = onClose
  dismissableRef.current = dismissable
  initialFocusPropRef.current = initialFocusRef

  // ─── Lifecycle: open/close ───────────────────────────────────────────
  useEffect(() => {
    if (!isOpen) return undefined

    previousFocusRef.current = document.activeElement

    // Lock body scroll
    const prevOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'

    // Move focus into the dialog after it mounts
    const focusTarget = initialFocusPropRef.current?.current
      || dialogRef.current?.querySelector(FOCUSABLE)
      || dialogRef.current
    focusTarget?.focus?.()

    const onKey = (e) => {
      if (e.key === 'Escape' && dismissableRef.current) {
        e.stopPropagation()
        onCloseRef.current?.()
        return
      }
      if (e.key === 'Tab' && dialogRef.current) {
        const focusables = dialogRef.current.querySelectorAll(FOCUSABLE)
        if (focusables.length === 0) {
          e.preventDefault()
          return
        }
        const first = focusables[0]
        const last  = focusables[focusables.length - 1]
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault()
          last.focus()
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault()
          first.focus()
        }
      }
    }

    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('keydown', onKey)
      document.body.style.overflow = prevOverflow
      previousFocusRef.current?.focus?.()
    }
  }, [isOpen])

  if (!isOpen) return null

  const onBackdrop = (e) => {
    if (!dismissable) return
    if (e.target === e.currentTarget) onClose?.()
  }

  return createPortal(
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
      onMouseDown={onBackdrop}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? titleId : undefined}
        tabIndex={-1}
        className={`
          w-full ${SIZES[size] || SIZES.md}
          bg-elevated text-ink-primary border border-border rounded-2xl shadow-token-xl
          max-h-[90vh] overflow-y-auto
          animate-bounce-gentle
          ${className}
        `}
      >
        {(title || dismissable) && (
          <div className="flex items-start justify-between gap-3 px-5 py-4 border-b border-border-subtle sticky top-0 bg-elevated z-10">
            {title && (
              <h2 id={titleId} className="text-base sm:text-lg font-semibold text-ink-primary">{title}</h2>
            )}
            {dismissable && (
              <button
                type="button"
                onClick={onClose}
                aria-label="Close"
                className="-mt-1 -me-1 p-1.5 rounded-lg text-ink-muted hover:text-ink-primary hover:bg-muted transition-colors flex-shrink-0"
              >
                <HiOutlineXMark className="w-5 h-5" />
              </button>
            )}
          </div>
        )}
        {children}
      </div>
    </div>,
    document.body
  )
}

Modal.Body = function ModalBody({ className = '', children }) {
  return <div className={`px-5 py-4 ${className}`}>{children}</div>
}

Modal.Footer = function ModalFooter({ className = '', children }) {
  return (
    <div className={`px-5 py-4 border-t border-border-subtle flex items-center justify-end gap-2 ${className}`}>
      {children}
    </div>
  )
}
